import { flash } from "@xola/ui-kit";
import { useUnmount } from "ahooks";
import { getPaymentLineItems, isExperiencePurchaseItem, isPackagePurchase, PurchaseActionOption } from "common";
import { isEmpty, sumBy } from "lodash";
import { useState } from "react";
import { useMutation } from "react-query";
import {
    Affiliate,
    CommunicationChannelType,
    Conversation,
    EmailTemplate,
    Event,
    ExperiencePurchaseItem,
    MerchandisePurchaseItem,
    PartnerObject,
    Purchase,
    PurchaseItemGuestStatus,
} from "types";
import { useDebouncedCallback } from "use-debounce";
import { api } from "@/api/api";
import { useDashboardTableStore } from "@/components/Dashboard/DashboardTable/useDashboardTableStore";
import { getRefundOptionAfterCancel } from "@/components/Dashboard/getRefundOptionAfterCancel";
import { RefundOption } from "@/components/Dashboard/RefundOption";
import { updatePurchaseTimeslotCache } from "@/components/Event/EditEventCapacityModal";
import { OnChangeArrivalContinueHandler } from "@/components/PurchaseModification/PurchaseChangeArrivalModal";
import { ReturnPaymentSource } from "@/components/PurchaseModification/Refund/PurchaseReturnPaymentModal";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { usePostCheckinWarningModal } from "@/hooks/usePostCheckinWarningModal";
import { useResource } from "@/hooks/useResource";
import { useUpdateEffect } from "@/hooks/useUpdateEffect";
import { hasBookings } from "@/models/Event";
import { getPurchaseBalanceForRefund } from "@/models/Purchase";
import { useModalStore } from "@/stores/dashboardFilterStore";
import { useUnassignGuidePromptStore } from "@/stores/unassignGuidePromptStore";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface UsePurchaseMenuStateProps {
    initialPurchase: Purchase;
    item: ExperiencePurchaseItem | MerchandisePurchaseItem;
    isVisibleGlobally: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    onClose?: () => void;
}

export const usePurchaseMenuState = ({
    initialPurchase,
    item,
    isVisibleGlobally,
    onOpenChange,
    onClose,
}: UsePurchaseMenuStateProps) => {
    const seller = getSeller();
    const { isGranted } = useAuthorization();
    const invalidate = useInvalidate();
    const setIsModalOpen = useModalStore((state) => state.setIsModalOpen);
    const openUnassignGuidePrompt = useUnassignGuidePromptStore((state) => state.open);

    // Common state
    const [shouldPrintStart, setShouldPrintStart] = useState(false);
    const [selectedOption, setSelectedOption] = useState<PurchaseActionOption | null>(null);
    const [isVisible, setIsVisible] = useState(isVisibleGlobally);
    const [remainingAction, setRemainingAction] = useState<PurchaseActionOption | null>(null);
    const [warningModalOpen, setWarningModalOpen] = useState(false);

    // Item-specific state
    const [selectedRefundOption, setSelectedRefundOption] = useState<RefundOption | null>(null);
    const [refundAmount, setRefundAmount] = useState<number>(0);
    const [returnPaymentSource, setReturnPaymentSource] = useState<ReturnPaymentSource | null>(null);
    const [modifiedPurchase, setModifiedPurchase] = useState<Purchase | null>(null);
    const [cancelReservationModalData, setCancelReservationModalData] = useState<{
        showRefundModal: boolean;
        newPurchase?: Purchase;
        events?: Event[];
    } | null>(null);

    const eventId = isExperiencePurchaseItem(item) ? item.event?.id : undefined;
    const { data: event } = useResource<Event>(["events", eventId], null, { enabled: !!eventId });

    const shouldRefetchPurchase = isPackagePurchase(initialPurchase) || shouldPrintStart;
    const { data: newPurchase, isLoading } = useResource<Purchase>(["purchases", initialPurchase.id], null, {
        enabled: shouldRefetchPurchase,
    });
    const purchase = newPurchase ?? initialPurchase;

    const isAffiliatePurchase = !!(purchase.partner?.id && purchase.partner?.object === PartnerObject.AFFILIATE);
    const { data: affiliate } = useResource<Affiliate>(["affiliates", purchase.partner?.id], null, {
        enabled: isAffiliatePurchase,
    });

    const enabledChannels = seller.preferences.communicationChannels?.enabledChannels ?? [];
    const shouldSendSms = enabledChannels.includes(CommunicationChannelType.SMS) && !!purchase.phone;
    const affiliateCanReceiveEmail = affiliate?.affiliateNotification?.enabled && !!affiliate?.email;
    const canSendEmail = !!purchase.customerEmail || affiliateCanReceiveEmail;
    const shouldShowUnassignGuide = event && event.guides.length > 0 && event.quantity.reserved === 0;
    const modalScopeId = item.event?.id ?? purchase.id;

    const { PostCheckInWarningModal, confirmPostCheckInWarning } = usePostCheckinWarningModal(item);

    const isSomethingOpen = isVisible || !!selectedOption || !!selectedRefundOption;

    useUpdateEffect(() => {
        onOpenChange?.(isSomethingOpen);
    }, [isSomethingOpen]);

    const updateModalState = useDebouncedCallback(() => {
        const shouldOpen = !!selectedOption || !!selectedRefundOption;
        setIsModalOpen(shouldOpen, modalScopeId);
    }, 100);

    useUnmount(() => {
        setIsModalOpen(false, modalScopeId);
    });

    useUpdateEffect(() => {
        updateModalState();
    }, [selectedOption, selectedRefundOption]);

    const { mutateAsync: createConversation } = useMutation(async () => {
        return api.post<Conversation>("/conversations", {
            purchase: { id: purchase.id },
            seller: { id: seller.id },
        });
    });

    const getConversationId = async () => {
        if (!purchase.conversation?.id) {
            const conversationData = await createConversation();
            return conversationData.data.id;
        }

        return purchase.conversation.id;
    };

    const {
        mutateAsync: sendConfirmationEmail,
        isLoading: isSendingConfirmationEmail,
        isSuccess: isConfirmationEmailSent,
    } = useMutation(async () => {
        try {
            const conversationId = await getConversationId();
            await api.post(`/conversations/${conversationId}/messages`, {
                meta: { template: EmailTemplate.PURCHASE_CONFIRMATION },
                to: { id: purchase.organizer?.id, type: "traveler" },
            });
            return true;
        } catch {
            flash.danger("Error sending confirmation email");
        }

        return false;
    });

    const {
        mutateAsync: sendConfirmationSms,
        isLoading: isSendingConfirmationSms,
        isSuccess: isConfirmationSmsSent,
    } = useMutation(async () => {
        try {
            const conversationId = await getConversationId();
            await api.post(`/conversations/${conversationId}/messages`, {
                meta: { template: EmailTemplate.PURCHASE_CONFIRMATION },
                to: { id: purchase.organizer?.id, type: "traveler" },
                type: "sms",
            });
            return true;
        } catch {
            console.error("Error sending confirmation sms");
        }

        return false;
    });

    const refreshPurchaseRow = () => {
        initialPurchase?.organizer?.id &&
            useDashboardTableStore.setState({ rowToRefresh: `${item.id}-${initialPurchase.organizer.id}` });
    };

    const handleModalClose = () => {
        setSelectedOption(null);
        onClose?.();
    };

    const handleClickOutside = () => {
        if (selectedOption || selectedRefundOption) {
            return;
        }

        setIsVisible(false);
        onClose?.();
    };

    const handlePurchaseInvalidate = () => {
        invalidate("purchases", purchase?.id);
        invalidate("purchaseItems", item.id);
        updatePurchaseTimeslotCache(purchase, isGranted);
        refreshPurchaseRow();
        onClose?.();
    };

    const handleFlushPurchaseCache = () => {
        if (modifiedPurchase) {
            updateCache("purchases", modifiedPurchase);
            updatePurchaseTimeslotCache(purchase, isGranted);

            if (item.event) {
                invalidate("events", item.event.id);
            }

            setModifiedPurchase(null);
            refreshPurchaseRow();
        }
    };

    const handleConfirmationMessage = async () => {
        const messagesSent = [];
        const messagesFailed = [];

        if (canSendEmail) {
            const isEmailSent = await sendConfirmationEmail();
            if (isEmailSent) {
                messagesSent.push(CommunicationChannelType.EMAIL);
            } else {
                messagesFailed.push(CommunicationChannelType.EMAIL);
            }
        }

        if (shouldSendSms) {
            const isSmsSent = await sendConfirmationSms();
            if (isSmsSent) {
                messagesSent.push(CommunicationChannelType.SMS);
            } else {
                messagesFailed.push(CommunicationChannelType.SMS);
            }
        }

        if (messagesSent.length > 0) {
            flash.success("Confirmation " + messagesSent.join(" and ") + " sent successfully");
        } else if (messagesFailed.length > 0) {
            flash.danger("Failed sending confirmation " + messagesFailed.join(" and "));
        }
        onClose?.();
    };

    const handleButtonClick = async (action: PurchaseActionOption, isMerchandise: boolean) => {
        setIsVisible(false);

        if (isMerchandise) {
            setSelectedOption(action);
            return;
        }

        const isCheckedIn =
            item.guestStatus === PurchaseItemGuestStatus.PARTIALLY_ARRIVED ||
            item.guestStatus === PurchaseItemGuestStatus.ARRIVED;

        if (isCheckedIn) {
            const confirmation = await confirmPostCheckInWarning();
            if (!confirmation) return;
        }

        // Before we open modal from action
        // we will check if it's needed to open warning modal
        const excludedPastDateActions = [PurchaseActionOption.RESEND_GRATUITY_EMAIL, PurchaseActionOption.MESSAGE];
        const now = new Date();
        if (
            isExperiencePurchaseItem(item) &&
            item.arrivalDateTime.isBefore(now) &&
            !excludedPastDateActions.includes(action)
        ) {
            setRemainingAction(action);
            setWarningModalOpen(true);
            return;
        }

        if (action === PurchaseActionOption.PRINT_RECEIPT) {
            setShouldPrintStart(true);
            return;
        }

        setSelectedOption(action);
    };

    const handleCloseAfterPurchaseChange = (newPurchase: Purchase | undefined) => {
        // Purchase will be undefined if custom line items haven`t changed
        const hasBalance = newPurchase?.balance && newPurchase.balance > 0.01;
        const isRefund = newPurchase && newPurchase.balance < 0;

        if (hasBalance) {
            setModifiedPurchase(newPurchase);
            setSelectedOption(PurchaseActionOption.COLLECT_BALANCE_OPTIONS);
            return;
        }

        if (isRefund && newPurchase) {
            setModifiedPurchase(newPurchase);
            setRefundAmount(getPurchaseBalanceForRefund(purchase, newPurchase));
            setSelectedRefundOption(RefundOption.RETURN_PAYMENT);
            setSelectedOption(null);
            return;
        }

        if (!hasBalance && !isRefund && shouldShowUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
            return;
        }

        setRefundAmount(0);
        setSelectedOption(null);
        // Invalidate purchase since we do not have to show next modal
        // If we have to show the next modal, we will invalidate the purchase after the payment is processed
        handlePurchaseInvalidate();
    };

    const handleChangeArrivalContinue: OnChangeArrivalContinueHandler = (previousEvent, updatedPurchase) => {
        if (!hasBookings(previousEvent) && !isEmpty(previousEvent.guides)) {
            setSelectedOption(null);
            // Invalidating now refetches the item with its new arrival, which re-keys the timeslot query the
            // roster drawer is bound to and closes it before the prompt can be answered.
            openUnassignGuidePrompt(previousEvent, handlePurchaseInvalidate);
            return;
        }

        if (updatedPurchase) {
            handleCloseAfterPurchaseChange(updatedPurchase);
        }
    };

    const resetSelectedRefundOption = () => {
        setReturnPaymentSource(null);
        setSelectedRefundOption(null);

        invalidate("purchases", purchase.id);
        if (item.event?.id) {
            invalidate("events", item.event.id);
        }
        updatePurchaseTimeslotCache(purchase, isGranted);
        onClose?.();
    };

    const handleCancelReservationClose = (showRefundModal: boolean, newPurchase?: Purchase, events?: Event[]) => {
        // Prefer the fresh, post-cancellation event(s) returned by the cancel API to decide whether the event is now
        // empty and its guide(s) should be removed. `shouldShowUnassignGuide` is derived from the cached event, which
        // is still stale (reserved > 0) at this point, so we only fall back to it when the cancel API returned no events.
        const shouldUnassignGuide =
            !showRefundModal &&
            (events?.some((event) => event.quantity.reserved === 0 && event.guides.length > 0) ??
                shouldShowUnassignGuide);

        if (shouldUnassignGuide) {
            setSelectedOption(PurchaseActionOption.UNASSIGN_GUIDE);
        } else {
            setSelectedOption(null);
        }

        if (newPurchase) {
            setModifiedPurchase(newPurchase);
        }
        /**
         * Show the refund modal for single item purchase
         * To show the refund modal, selectedOption should be set to null and selectedRefundOption should be set return payment
         * Also calculate refund amount
         *
         * */
        if (newPurchase && showRefundModal) {
            const amount =
                newPurchase.balance < 0 ? newPurchase.balance : sumBy(getPaymentLineItems(newPurchase), "amount");
            setRefundAmount(Math.abs(amount));

            setSelectedRefundOption(getRefundOptionAfterCancel(newPurchase, item));
        }

        // When we are about to show the unassign-guide prompt, skip invalidation here: `handlePurchaseInvalidate`
        // calls `onClose`, which unmounts the menu (and the prompt) before it can render. The prompt's own onClose
        // (`handleUnassignGuidePromptClose`) invalidates once the user responds.
        if (!shouldUnassignGuide) {
            if (newPurchase && !showRefundModal) {
                // Invalidate purchase immediately since we do not have to show the refund modal
                // If we have to show the refund modal, we will invalidate the purchase after the refund is processed
                handlePurchaseInvalidate();
            }

            if (!newPurchase && !showRefundModal) {
                setSelectedOption(null);
                setSelectedRefundOption(null);
                handlePurchaseInvalidate();
            }
        }
    };

    const menuState = {
        purchase,
        event,
        isLoading,
        shouldPrintStart,
        selectedOption,
        isVisible,
        remainingAction,
        warningModalOpen,
        selectedRefundOption,
        refundAmount,
        returnPaymentSource,
        modifiedPurchase,
        cancelReservationModalData,
        shouldShowUnassignGuide,
        canSendEmail,
        shouldSendSms,
        isSendingConfirmationEmail,
        isSendingConfirmationSms,
        isConfirmationEmailSent,
        isConfirmationSmsSent,
        shouldRefetchPurchase,
        PostCheckInWarningModal,
    };

    const menuActions = {
        setShouldPrintStart,
        setSelectedOption,
        setIsVisible,
        setWarningModalOpen,
        setRemainingAction,
        setSelectedRefundOption,
        setRefundAmount,
        setReturnPaymentSource,
        setModifiedPurchase,
        setCancelReservationModalData,
        handleButtonClick,
        handleClickOutside,
        handleModalClose,
        handleConfirmationMessage,
        handleCloseAfterPurchaseChange,
        handleChangeArrivalContinue,
        handleFlushPurchaseCache,
        handlePurchaseInvalidate,
        resetSelectedRefundOption,
        handleCancelReservationClose,
        refreshPurchaseRow,
    };

    return { menuState, menuActions };
};
